import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { CartItem, CartState } from "../../common/types/interfaces";

interface CartSliceState extends CartState {
  cutleryByCluster: Record<number, number>;
}

const initialState: CartSliceState = {
  items: [],
  total: 0,
  cutleryByCluster: {},
};

const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => total + item.price * item.quantity, 0);
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<Omit<CartItem, "quantity">>) => {
      const existingItem = state.items.find(
        (item: CartItem) =>
          item.id === action.payload.id &&
          item.clusterId === action.payload.clusterId &&
          item.isGift === action.payload.isGift,
      );

      if (existingItem) {
        existingItem.quantity += existingItem.quantityStep;
      } else {
        state.items.push({
          ...action.payload,
          quantity: action.payload.minQuantity,
        });
      }
      state.total = calculateTotal(state.items);
    },

    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.total = calculateTotal(state.items);
    },

    increaseQuantity: (state, action: PayloadAction<string>) => {
      const item = state.items.find(
        (item: CartItem) => item.id === action.payload,
      );
      if (item) {
        if (item.isGift) return;
        item.quantity += item.quantityStep;
        state.total = calculateTotal(state.items);
      }
    },

    decreaseQuantity: (state, action: PayloadAction<string>) => {
      const item = state.items.find(
        (item: CartItem) => item.id === action.payload,
      );
      if (item) {
        if (item.isGift) return;
        const newQuantity = item.quantity - item.quantityStep;

        if (newQuantity < item.minQuantity) {
          state.items = state.items.filter((i) => i.id !== action.payload);
        } else {
          item.quantity = newQuantity;
        }
        state.total = calculateTotal(state.items);
      }
    },

    clearCart: (state) => {
      state.items = [];
      state.total = 0;
      state.cutleryByCluster = {};
    },

    setCart: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
      state.total = calculateTotal(action.payload);
    },

    addGift: (state, action: PayloadAction<CartItem>) => {
      state.items = state.items.filter((item) => !item.isGift);
      state.items.push(action.payload);
      state.total = calculateTotal(state.items);
    },

    removeGift: (state) => {
      state.items = state.items.filter((item) => !item.isGift);
      state.total = calculateTotal(state.items);
    },

    incrementCutlery: (state, action: PayloadAction<number>) => {
      const clusterId = action.payload;
      state.cutleryByCluster[clusterId] =
        (state.cutleryByCluster[clusterId] || 0) + 1;
    },

    decrementCutlery: (state, action: PayloadAction<number>) => {
      const clusterId = action.payload;
      const current = state.cutleryByCluster[clusterId] || 0;
      if (current <= 1) {
        delete state.cutleryByCluster[clusterId];
      } else {
        state.cutleryByCluster[clusterId] = current - 1;
      }
    },

    setCutlery: (
      state,
      action: PayloadAction<{ clusterId: number; count: number }>,
    ) => {
      const { clusterId, count } = action.payload;
      if (count <= 0) {
        delete state.cutleryByCluster[clusterId];
      } else {
        state.cutleryByCluster[clusterId] = count;
      }
    },

    clearCutlery: (state, action: PayloadAction<number>) => {
      delete state.cutleryByCluster[action.payload];
    },

    removeClusterItems: (state, action: PayloadAction<number>) => {
      const clusterId = action.payload;
      state.items = state.items.filter(
        (item) => Number(item.clusterId) !== Number(clusterId),
      );
      state.total = calculateTotal(state.items);
      delete state.cutleryByCluster[clusterId];
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  increaseQuantity,
  decreaseQuantity,
  clearCart,
  setCart,
  addGift,
  removeGift,
  incrementCutlery,
  decrementCutlery,
  setCutlery,
  clearCutlery,
  removeClusterItems,
} = cartSlice.actions;

export const selectCart = (state: RootState) => state.cart;
export const selectCartItems = (state: RootState) => state.cart.items;
export const selectCutleryByCluster = (state: RootState) =>
  state.cart.cutleryByCluster;

export default cartSlice.reducer;
